/**
 * Data transformation utilities for quick access cards
 * Handles conversion of API data to card-compatible format with relative timestamps and stats
 */

export type RelativeTimeString = string;
export type ActivityLevel = 'high' | 'medium' | 'low' | 'none';

/**
 * Format timestamp to relative time string (e.g., "2 hours ago")
 * Returns "Never" if no timestamp is provided or if the timestamp is invalid
 * 
 * Supports various time ranges:
 * - Just now: within 1 minute
 * - Minutes ago: within 1 hour
 * - Hours ago: within 24 hours
 * - Days ago: within 7 days
 * - Weeks ago: within 4 weeks
 * - Months ago: within 12 months
 * - Years ago: 1+ years
 * 
 * @param timestamp - ISO 8601 timestamp string, Date object, or null/undefined
 * @returns Formatted relative time string (e.g., "2 hours ago", "Just now", "Never")
 * 
 * @example
 * formatRelativeTime('2024-01-12T20:00:00Z') // "2 hours ago"
 * formatRelativeTime(new Date(Date.now() - 3600000)) // "1 hour ago"
 * formatRelativeTime(null) // "Never"
 */
export function formatRelativeTime(
  timestamp?: string | Date | null
): RelativeTimeString {
  if (!timestamp) {
    return 'Never';
  }

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Never';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Just now (within 1 minute)
  if (diffMins < 1) {
    return 'Just now';
  }

  // Minutes ago
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  }

  // Hours ago
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }

  // Days ago
  if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

  // Weeks ago
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  }

  // Months ago
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  }

  // Years ago
  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
}

/**
 * Determine activity level based on recency of timestamp
 * Used to display visual activity indicators on cards
 * 
 * Activity levels:
 * - high: within last 24 hours (recent activity)
 * - medium: within last 72 hours (moderate activity)
 * - low: older than 72 hours (stale activity)
 * - none: no timestamp provided (no activity)
 * 
 * @param timestamp - ISO 8601 timestamp string, Date object, or null/undefined
 * @returns Activity level indicator: 'high' | 'medium' | 'low' | 'none'
 * 
 * @example
 * getActivityLevel(new Date(Date.now() - 3600000)) // "high" (1 hour ago)
 * getActivityLevel(new Date(Date.now() - 86400000 * 2)) // "medium" (2 days ago)
 * getActivityLevel(new Date(Date.now() - 86400000 * 4)) // "low" (4 days ago)
 * getActivityLevel(null) // "none"
 */
export function getActivityLevel(
  timestamp?: string | Date | null
): ActivityLevel {
  if (!timestamp) {
    return 'none';
  }

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'none';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return 'high';
  }
  if (diffHours < 72) {
    return 'medium';
  }
  return 'low';
}

/**
 * Stats entry structure used in card display
 */
export interface CardStat {
  label: string;
  value: string | number;
}

/**
 * Transform mailer data into stats array format
 * Handles null/undefined data gracefully
 * 
 * @param lastSentAt - ISO 8601 timestamp of last sent email
 * @returns Array of stats objects or undefined if no data
 * 
 * @example
 * transformMailerStats('2024-01-12T20:00:00Z')
 * // Returns: [{ label: 'Last sent', value: '2 hours ago' }]
 * 
 * transformMailerStats(null)
 * // Returns: undefined
 */
export function transformMailerStats(lastSentAt?: string | null): CardStat[] | undefined {
  if (!lastSentAt) {
    return undefined;
  }

  return [
    {
      label: 'Last sent',
      value: formatRelativeTime(lastSentAt),
    },
  ];
}

/**
 * Campaign stats structure
 */
export interface CampaignStats {
  activeCampaigns: number;
  totalCampaigns: number;
  lastUpdatedAt: string | null;
}

/**
 * Transform campaign array into stats object
 * Calculates active and total campaigns from array
 * Handles empty arrays and null data gracefully
 * 
 * @param campaigns - Array of campaign objects with createdAt property
 * @returns Stats object with campaign counts and last update time, or null if no campaigns
 * 
 * @example
 * const campaigns = [
 *   { id: '1', createdAt: '2024-01-12T20:00:00Z', ... },
 *   { id: '2', createdAt: '2024-01-11T10:00:00Z', ... }
 * ];
 * transformCampaignStats(campaigns)
 * // Returns: {
 * //   activeCampaigns: 2,
 * //   totalCampaigns: 2,
 * //   lastUpdatedAt: '2024-01-12T20:00:00Z'
 * // }
 */
export function transformCampaignStats(
  campaigns?: Array<{ createdAt?: string }> | null
): CampaignStats | null {
  if (!campaigns || campaigns.length === 0) {
    return null;
  }

  const totalCampaigns = campaigns.length;
  // All campaigns are considered active in current implementation
  // Can be enhanced later if campaigns have status field
  const activeCampaigns = totalCampaigns;
  const lastUpdatedAt = campaigns[0]?.createdAt || null;

  return {
    activeCampaigns,
    totalCampaigns,
    lastUpdatedAt,
  };
}

/**
 * Transform campaign stats into card stats array format
 * 
 * @param stats - Campaign stats object from transformCampaignStats
 * @returns Array of stats objects or undefined if no stats
 * 
 * @example
 * const stats = {
 *   activeCampaigns: 5,
 *   totalCampaigns: 10,
 *   lastUpdatedAt: '2024-01-12T20:00:00Z'
 * };
 * transformCampaignCardStats(stats)
 * // Returns: [
 * //   { label: 'Active campaigns', value: 5 },
 * //   { label: 'Total campaigns', value: 10 }
 * // ]
 */
export function transformCampaignCardStats(stats?: CampaignStats | null): CardStat[] | undefined {
  if (!stats) {
    return undefined;
  }

  return [
    {
      label: 'Active campaigns',
      value: stats.activeCampaigns,
    },
    {
      label: 'Total campaigns',
      value: stats.totalCampaigns,
    },
  ];
}
